import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { getApiCall } from "../../../service/httpCall";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { LICENCE_HISTORY_LIST } from "../../../service/url";
import Loader from "../../../components/partials/components/loader";
import exportData from "../../../utilities/export";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import { errorCheck } from "../auth/auth-utils";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import "../../../assets/custom/css/custom.css";
import { LICENCE_LABEL } from "../../../utilities/constant";
import { exportToCSV, hasPermission } from '../../../utilities/helpers';
import { getUser } from "../../../utilities/setting";
import { Download, ArrowUpCircle, FileText, User, Building2, UserCheck, UserX } from "lucide-react";
import {
  FaBroadcastTower,
  FaBuilding,
  FaUsers,
  FaUserCheck,
  FaPhone,
  FaArrowRight,
} from "react-icons/fa";

const getTodayStr = () => new Date().toLocaleDateString("en-CA");
const getFirstOfMonthStr = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString("en-CA");
};
const getSixMonthsAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toLocaleDateString("en-CA");
};

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};


const ICON_MAP = {
  channel: <FaBroadcastTower />,
  company: <FaBuilding />,
  live_agent: <FaUserCheck />,
  role_ccagent: <FaUsers />,
  role_ccadmin: <FaPhone />,
};

export default function LicenceHistoryList( { companyId , callcenterId ,displayName  }) {
  const navigate = useNavigate();
  const [operationList, setOperationList] = useState([]);
  const [originalDataList, setOriginalDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [fromDateValue, setFromDateValue] = useState(getFirstOfMonthStr());
  const [toDateValue, setToDateValue] = useState(getTodayStr());
  const [appliedFilters, setAppliedFilters] = useState({
    from: getFirstOfMonthStr(),
    to: getTodayStr(),
  });

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);


  const handleExport = async () => {
  try {
    setButtonDisable(true);

    exportToCSV({
      fileName: "licence-history",
      data: operationList,
      columns: [
        {
          header: "#",
          accessor: "key",
        },
        {
          header: "Action",
          accessor: (row) =>
            row.action?.toUpperCase() || "-",
        },
        {
          header: "Username",
          accessor: (row) =>
            row.user?.username || "-",
        },
        {
          header: "Company",
          accessor: (row) =>
            row.company?.name || "-",
        },
        {
          header: "Call Center",
          accessor: (row) =>
            row.callcenter?.name || "-",
        },
        {
          header: "Changes",
          accessor: (row) => {
            const oldLic = row.old_licence || {};
            const newLic = row.updated_licence || {};

            return Object.keys(newLic)
              .map(key =>
                `${key}: ${oldLic[key] ?? 0} → ${newLic[key] ?? 0}`
              )
              .join(" | ");
          },
        },
        {
          header: "Date & Time",
          accessor: "created_at_formatted",
        },
      ],
    });

  } finally {
    setButtonDisable(false);
  }
};


  const fetchLicenceHistory = async (start = fromDateValue, end = toDateValue , cId = companyId , ccId = callcenterId) => {
    setLoading(true);

    const queryParams = new URLSearchParams();
    if (start) queryParams.append("start_date", start);
    if (end) queryParams.append("end_date", end);
      if (cId) queryParams.append("company_id", cId); 
       if (ccId) queryParams.append("callcenter_id", ccId); 

    const finalUrl = `${LICENCE_HISTORY_LIST}${queryParams.toString() ? "?" + queryParams.toString() : ""}`;

    try {
      const resp = await getApiCall(finalUrl);
      const data = Array.isArray(resp) ? resp : (resp?.data || []); 
      // console.log("API Response:", resp); 
      onOperationListSuccess(data);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = () => {
    fetchLicenceHistory(fromDateValue, toDateValue);

    setAppliedFilters({
      from: fromDateValue,
      to: toDateValue,
    });
  };

useEffect(() => {
    setOperationList([]);
    setOriginalDataList([]);
    const start = getFirstOfMonthStr();
    const end = getTodayStr();
    setFromDateValue(start);
    setToDateValue(end);
    setAppliedFilters({ from: start, to: end });
    fetchLicenceHistory(start, end, companyId, callcenterId);
}, [companyId, callcenterId]);


  function onOperationListSuccess(resp) {
    const updatedList = resp.map((val, idx) => {
      return {
        ...val,
        key: idx + 1,
       company: val.company ? val.company : { 
        name: displayName || "-" 
      },
        created_at_formatted: new Date(val.created_at).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true
        }),
      };
    });

    setOperationList(updatedList);
    setOriginalDataList(updatedList);
  }

  function onError(err) {
    if (typeof err !== "object" && err.includes("TokenExpiredError")) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

    useEffect(() => {
      const user = getUser();
        if (user) {
          if (user?.acl_settings) {
            setModuleOperationsForReadPermissionCheck(JSON.parse(user.acl_settings.module_operations));
          }
        }
    }, []);

    useEffect(() => {
      const onAclRefreshed = () => {
        const user = getUser();
        if (user?.acl_settings?.module_operations) {
          try {
            const parsed = typeof user.acl_settings.module_operations === "string"
              ? JSON.parse(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
            setModuleOperationsForReadPermissionCheck(parsed);
          } catch (e) {
            console.log("Failed to apply refreshed ACL", e);
          }
        }
      };
      window.addEventListener("acl-refreshed", onAclRefreshed);
      return () => window.removeEventListener("acl-refreshed", onAclRefreshed);
    }, []);

  const licenceChangeBody = (rowData) => {
    const oldLic = rowData.old_licence || {};
    const newLic = rowData.updated_licence || {};

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {Object.keys(LICENCE_LABEL).filter((key) => {
          if (rowData.callcenter && key === "role_ccadmin") return false;
          if (rowData.callcenter && key === "company") return false;
          return true;
        }).map((key) => {
          const oldVal = oldLic[key] ?? 0;
          const newVal = newLic[key] ?? 0;
          const changed = oldVal !== newVal;
          const changeClassName =
            newVal > oldVal
              ? "licence-change-card licence-change-up"
              : newVal < oldVal
                ? "licence-change-card licence-change-down"
                : "licence-change-card";

          return (
            <div
              key={key}
              className={changeClassName}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "8px",
                padding: "6px 10px",
                fontSize: "13px",
              }}
            >
              {/* Left */}
              <div
                className="licence-change-label"
                style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, marginRight: "3px" }}
              >
                <span style={{ color: "#6366f1", fontSize: "14px" }}>
                  {ICON_MAP[key]}
                </span>
                {LICENCE_LABEL[key]}
              </div>

              {/* Right */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span
                  className="licence-change-value"
                  style={{
                    background: "#fee2e2",
                    color: "#dc2626",
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontWeight: 600,
                  }}
                >
                  {oldVal}
                </span>

                <span className="licence-change-arrow">
                  <FaArrowRight size={14} color="currentColor" />
                </span>

                <span
                  className="licence-change-value"
                  style={{
                    background: "#dcfce7",
                    color: "#16a34a",
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontWeight: 600,
                  }}
                >
                  {newVal}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const tableRows = useMemo(() => {
    const list = Array.isArray(originalDataList) ? originalDataList : [];

    return list
      .map((r, idx) => {
        const createdDate = safeDate(r?.created_at);
        const createdTs = createdDate ? createdDate.getTime() : 0;

        return {
          ...r,
          key: r?.key ?? idx + 1,
          _createdTs: createdTs,
          _createdFromDate: createdDate,
        };
      })
      .sort((a, b) => (b._createdTs || 0) - (a._createdTs || 0));
  }, [originalDataList]);

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

      const start = appliedFilters.from
      ? new Date(`${appliedFilters.from}T00:00:00.000`).getTime()
      : null;

    const end = appliedFilters.to
      ? new Date(`${appliedFilters.to}T23:59:59.999`).getTime()
      : null;


    return tableRows.filter((r) => {
      // Date filter
      if (start != null && (r?._createdTs || 0) < start) return false;
      if (end != null && (r?._createdTs || 0) > end) return false;

      // Text search
      if (query) {
        const action = r?.action?.toLowerCase() || "";
        const username = r?.user?.username?.toLowerCase() || "";
        const company = r?.company?.name?.toLowerCase() || "";
        const callcenter = r?.callcenter?.name?.toLowerCase() || "";
        const created = r?.created_at_formatted?.toLowerCase() || "";

        if (
          action.includes(query) ||
          username.includes(query) ||
          company.includes(query) ||
          callcenter.includes(query) ||
          created.includes(query)
        ) {
          return true;
        }

        const oldLic = r.old_licence || {};
        const newLic = r.updated_licence || {};
        for (const key of Object.keys(LICENCE_LABEL)) {
          if (String(oldLic[key] ?? "").toLowerCase().includes(query)) return true;
          if (String(newLic[key] ?? "").toLowerCase().includes(query)) return true;
        }

        return false;
      }

      return true;
    });
  }, [searchValue, tableRows, appliedFilters]);


  if (loading) {
    return <Loader />;
  }


if (companyId || callcenterId) {
  return (
    <>
      <div
        className="table-responsive broad-scrollbar"
        style={{ overflowX: "auto" }}
      >
        <DataTable
          value={tableRows}
          paginator
          rows={10}
          emptyMessage="No licence history found."
          tableStyle={{ minWidth: "50rem" }}
        >
          <Column field="key" header="#" style={{ width: "5%" }} />

          <Column
            header="Action"
            body={(rowData) => {
              const action = rowData.action?.toLowerCase();
              const isCreated = action === "created";
              const isLiveLogin = action === "live_agent_login";
              const isLiveLogout = action === "live_agent_logout";
              const isChannelDeducted = action === "channel_usage";
              return (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "4px 10px", borderRadius: "20px", fontSize: "12px",
                  fontWeight: 500,
                  backgroundColor: isCreated ? "#3b82f6" : isLiveLogin ? "#16a34a"
                    : isLiveLogout ? "#dc2626" : isChannelDeducted ? "#9f26dc" : "#00b126",
                  color: "white",
                }}>
                  {isCreated ? <FileText size={14} /> : isLiveLogin ? <UserCheck size={14} />
                    : isLiveLogout ? <UserX size={14} /> : isChannelDeducted
                    ? <FaBroadcastTower size={14} /> : <ArrowUpCircle size={14} />}
                  {rowData.action?.toUpperCase()}
                </span>
              );
            }}
            style={{ width: "10%" }}
          />

          <Column
            header="Changes"
            body={licenceChangeBody}
            style={{ width: "40%" }}
          />

          <Column
            header="Company"
            body={(rowData) =>{
    const displayName = rowData.company?.name || rowData.callcenter?.name || rowData.name;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {displayName ? (
          <>
            <Building2 size={16} style={{ color: "#6b7280", flexShrink: 0 }} />
            <span>{displayName}</span>
          </>
        ) : (
          <span>-</span>
       
         )} </div>
            )}}
            style={{ width: "20%" }}
          />

          <Column field="created_at_formatted" header="Date & Time" style={{ width: "25%" }} />
        </DataTable>
      </div>

      <style>{`
        .broad-scrollbar::-webkit-scrollbar { height: 10px; }
        .broad-scrollbar::-webkit-scrollbar-thumb { background-color: #888; border-radius: 10px; }
      `}</style>
    </>
  );
}

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="header-title">
                <h4 className="card-title">Licence History</h4>
              </div>
            </Card.Header>
            <Card.Body className="px-0">
              <Row className="mb-3 px-3 g-2 align-items-center flex-nowrap">
                <Col xs="auto">
                  <Form.Control
                    type="text"
                    placeholder="Search values..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ height: "40px", width: "200px" }}
                  />
                </Col>

                <Col xs="auto">
                  <Form.Control
                    type="date"
                    value={fromDateValue}
                    min={getSixMonthsAgoStr()}
                    max={getTodayStr()}
                    onChange={(e) => setFromDateValue(e.target.value)}
                    style={{ height: "40px", width: "170px" }}
                  />
                </Col>

                <Col xs="auto">
                  <Form.Control
                    type="date"
                    value={toDateValue}
                    min={fromDateValue}
                    max={getTodayStr()}
                    onChange={(e) => setToDateValue(e.target.value)}
                    style={{ height: "40px", width: "170px" }}
                  />
                </Col>

                <Col xs="auto">
                  <Button
                    variant="primary"
                    className="p-button-active p-button-sm d-flex gap-2"
                    onClick={handleFilterClick}
                  >
                    <i className="pi pi-filter me-2"></i> Filter
                  </Button>
                </Col>

                <Col xs="auto">
                  <Button
                    variant="primary"
                    className="p-button-active p-button-sm d-flex gap-2"
                    onClick={() => {
                      const start = getFirstOfMonthStr();
                      const end = getTodayStr();
                      setSearchValue("");
                      setFromDateValue(start);
                      setToDateValue(end);
                      fetchLicenceHistory(start, end);
                    }}
                  >
                    Refresh
                  </Button>
                </Col>

                <Col className="d-flex justify-content-end align-items-center">
                  {operationList.length > 0 && (
                    <Button
                      onClick={handleExport}
                      className="p-button-active p-button-sm"
                      disabled={buttonDisable}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 16px",
                      }}
                    >
                      {buttonDisable ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <Download size={16} />
                      )}
                      Export Data
                    </Button>
                  )}
                </Col>
              </Row>


              <div
                className="table-responsive broad-scrollbar"
                style={{ overflowX: "auto" }}
              >
                <DataTable
                  value={filteredRows}
                  paginator
                  rows={10}
                  emptyMessage="No licence history found."
                  tableStyle={{ minWidth: "50rem" }}
                >
                  <Column field="key" header="#" style={{ width: "5%" }} />

                  <Column
                    header="Action"
                    body={(rowData) => {
                      const action = rowData.action?.toLowerCase();
                      const isCreated = action === "created" || action === "agent_created" || action === "bulk_agent_created";
                      const isDeleted = action === "agent_deleted";
                      const isLiveLogin = action === "live_agent_login";
                      const isLiveLogout = action === "live_agent_logout";
                      const isChannelDeducted = action === "channel_usage";
                      return (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 10px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: 500,
                            backgroundColor: isCreated
                              ? "#3b82f6"
                              : isDeleted
                              ? "#ef4444"
                              : isLiveLogin
                              ? "#16a34a"
                              : isLiveLogout
                              ? "#dc2626"
                              : isChannelDeducted
                              ? "#9f26dc"
                              : "#00b126",
                            color: "white",
                          }}
                        >
                          {isCreated ? (
                            <FileText size={14} />
                          ) : isDeleted ? (
                            <UserX size={14} />
                          ) : isLiveLogin ? (
                            <UserCheck size={14} />
                          ) : isLiveLogout ? (
                            <UserX size={14} />
                          ) : isChannelDeducted ? (
                            <FaBroadcastTower size={14} />
                          ) : (
                            <ArrowUpCircle size={14} />
                          )}
                          {rowData.action?.toUpperCase()}
                        </span>
                      );
                    }}
                    style={{ width: "10%" }}
                  />

                  <Column
                    header="Changes"
                    body={licenceChangeBody}
                    style={{ width: "50%" }}
                  />

                  <Column
                    header="Username"
                    body={(rowData) => (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <User size={16} style={{ color: "#6b7280", flexShrink: 0 }} />
                        <span>{rowData.user?.username || "-"}</span>
                      </div>
                    )}
                    style={{ width: "20%" }}
                  />
                  <Column
                    header="Company"
                    body={(rowData) => (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {rowData.company?.name ? (
                          <>
                            <Building2 size={16} style={{ color: "#6b7280", flexShrink: 0 }} />
                            <span>{rowData.company.name}</span>
                          </>
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    )}
                    style={{ width: "20%" }}
                  />
                  <Column
                    field="callcenter.name"
                    header="Call Center"
                    style={{ width: "20%" }}
                  />

                  <Column
                    field="created_at_formatted"
                    header="Date & Time"
                    style={{ width: "25%" }}
                  />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <style>
        {`
          .broad-scrollbar::-webkit-scrollbar {
            height: 10px;
          }
          .broad-scrollbar::-webkit-scrollbar-thumb {
            background-color: #888;
            border-radius: 10px;
          }
        `}
      </style>
    </>
  );
}

